import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, ChevronDown, Loader2, Plus, Search, Trash2 } from 'lucide-react';
import {
  getPatientPackages,
  deletePatientPackage,
  addPackageItem,
  type PatientPackage,
} from '@/services/packages.service';
import {
  fmtStoredDay,
  groupPackageProcedures,
  pacificToday,
  procedureStatus,
  type ProcedureGroup,
  type ProcedureStatus,
} from './packageProcedures';
import { searchProducts, type ProductSearchResult } from '@/services/memberships.service';
import { getErrorMessage } from '@/services/apiClient';
import { getPatientProductTypes } from '@/services/patientProductTypes.service';
import { listPatientFacilities } from '@/services/patients.service';
import { listDiscounts, type PaymentDiscount } from '@/services/payments.service';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/hooks/useAuth';
import { PaymentDetailModal } from '@/pages/payments/PaymentDetailModal';
import {
  CountBadge,
  ProductGroupCard,
  SubListToggle,
  toArray,
  type ProductGroup,
  type ProductItem,
  type ProductTypesResponse,
} from '@/components/patients/PatientProductsDisplay';
import { DatePicker } from '@/components/ui/DatePicker';

/* ── Utility helpers ───────────────────────────────────────────────── */

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function fmtCurrency(value: number | null | undefined): string {
  if (value == null) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function getGroupExpireDate(group: ProductGroup): string | null {
  const allItems = [
    ...toArray<ProductItem>(group.available),
    ...toArray<ProductItem>(group.appointments),
  ];
  for (const item of allItems) {
    if (item.expireDate && item.expireDate !== '0000-00-00 00:00:00') {
      return item.expireDate;
    }
  }
  return null;
}

function fmtShortDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Los_Angeles' });
}

/* ── Filter products by package ────────────────────────────────────── */

function filterProductsByPackage(
  data: ProductTypesResponse,
  packageId: number,
): { types: ProductGroup[]; others: ProductGroup[] } {
  const filterItems = (items: ProductItem[]) =>
    items.filter((i) => i.packageID != null && i.packageID === packageId);

  function filterGroup(group: ProductGroup): ProductGroup | null {
    const available = filterItems(toArray<ProductItem>(group.available));
    const appointments = filterItems(toArray<ProductItem>(group.appointments));
    const comped = filterItems(toArray<ProductItem>(group.comped));
    const purchase = filterItems(toArray<ProductItem>(group.purchase));
    const unavailable = filterItems(toArray<ProductItem>(group.unavailable));
    // onHold must be filtered by package too — otherwise the `...group` spread
    // below leaks the patient's (membership-based) on-hold items into the
    // package view, making package products appear "on hold" like a membership.
    // Package products are never put on hold by the backend, so this is empty.
    const onHold = filterItems(toArray<ProductItem>(group.onHold));

    const total = available.length + appointments.length
      + comped.length + purchase.length + unavailable.length + onHold.length;
    if (total === 0) return null;

    const usedQty = unavailable
      .filter((p) => p.usedDate && p.usedDate !== '0000-00-00 00:00:00')
      .reduce((s, p) => s + p.quantity, 0);
    const expiredOrDeleted = unavailable.filter(
      (p) => !p.usedDate || p.usedDate === '0000-00-00 00:00:00',
    );
    const expireQty = expiredOrDeleted
      .filter((p) => !p.deleteDate || p.deleteDate === '0000-00-00 00:00:00')
      .reduce((s, p) => s + p.quantity, 0);
    const deleteQty = expiredOrDeleted
      .filter((p) => p.deleteDate && p.deleteDate !== '0000-00-00 00:00:00')
      .reduce((s, p) => s + p.quantity, 0);

    return {
      ...group,
      available,
      upcoming: [],
      appointments,
      comped,
      compedQuantity: comped.reduce((s, p) => s + p.quantity, 0),
      purchase,
      unavailable,
      onHold,
      onHoldQuantity: onHold.reduce((s, p) => s + p.quantity, 0),
      availableQuantity: available.reduce((s, p) => s + p.quantity, 0),
      upcomingQuantity: 0,
      appointmentsQuantity: appointments.reduce((s, p) => s + p.quantity, 0),
      usedQuantity: usedQty,
      expireQuantity: expireQty,
      deleteQuantity: deleteQty,
    };
  }

  const rawTypes = data?.data?.['product-types'] ?? {};
  const rawOthers = data?.data?.['product-others'] ?? {};

  const types = Object.values(rawTypes)
    .map(filterGroup)
    .filter((g): g is ProductGroup => g !== null);
  const others = Object.values(rawOthers)
    .map(filterGroup)
    .filter((g): g is ProductGroup => g !== null);

  return { types, others };
}

/* ── Procedures sold with the package ──────────────────────────────── */

function ProcedureRow({ children }: { children: React.ReactNode }) {
  return <div className="rounded bg-gray-50 px-2 py-1.5 text-xs text-gray-700">{children}</div>;
}

/** One procedure the package was sold with, laid out like a product card. */
function ProcedureGroupCard({ group }: { group: ProcedureGroup }) {
  const [open, setOpen] = useState(false);
  const today = pacificToday();
  const withStatus = (s: ProcedureStatus) => group.items.filter((i) => procedureStatus(i, today) === s);
  const available = withStatus('available');
  const used = withStatus('used');
  const expired = withStatus('expired');
  const removed = withStatus('deleted');

  return (
    <div className="mb-2 rounded border border-gray-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-gray-50"
      >
        <span className={`text-xs text-gray-400 transition-transform ${open ? 'rotate-90' : ''}`}>
          &#9654;
        </span>
        <span className="flex-1 text-sm font-semibold text-gray-800">
          {group.name}
          {group.category && <span className="ml-2 text-xs font-normal text-gray-400">{group.category}</span>}
        </span>
        <CountBadge count={available.length} icon={<ArrowRight className="h-3 w-3" />} title="Available" bg="bg-blue-100" text="text-blue-800" large />
      </button>

      {open && (
        <div className="border-t border-gray-100 px-3 py-2">
          <SubListToggle title="Available" count={available.length} countColor="text-[#357bb8]" defaultOpen>
            {available.map((i) => (
              <ProcedureRow key={i.patientPackageItemId}>
                {i.expireDate ? `Expires ${fmtStoredDay(i.expireDate)}` : 'Does not expire'}
              </ProcedureRow>
            ))}
          </SubListToggle>
          <SubListToggle title="Used" count={used.length}>
            {used.map((i) => (
              <ProcedureRow key={i.patientPackageItemId}>Used {fmtStoredDay(i.usedDate)}</ProcedureRow>
            ))}
          </SubListToggle>
          <SubListToggle title="Expired / Deleted" count={expired.length + removed.length}>
            {expired.map((i) => (
              <ProcedureRow key={i.patientPackageItemId}>Expired {fmtStoredDay(i.expireDate)}</ProcedureRow>
            ))}
            {removed.map((i) => (
              <ProcedureRow key={i.patientPackageItemId}>Deleted {fmtStoredDay(i.deleteDate)}</ProcedureRow>
            ))}
          </SubListToggle>
        </div>
      )}
    </div>
  );
}

/* ── Collapsible Section ──────────────────────────────────────────── */

function CollapsibleSection({ title, defaultOpen = false, headerExtra, children }: {
  title: string;
  defaultOpen?: boolean;
  headerExtra?: React.ReactNode;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-lg border border-slate-200">
      <div className="flex items-center bg-slate-50 px-4 py-2">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex flex-1 items-center gap-2 text-left"
        >
          <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${open ? '' : '-rotate-90'}`} />
          <h3 className="text-sm font-semibold text-slate-700">{title}</h3>
        </button>
        {headerExtra && (
          <div className="flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            {headerExtra}
          </div>
        )}
      </div>
      {open && <div className="border-t border-slate-200">{children}</div>}
    </div>
  );
}

/* ── Add Product Modal ─────────────────────────────────────────────── */

function AddProductModal({
  packageId,
  open,
  onClose,
  onSaved,
}: {
  packageId: number;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProductSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<ProductSearchResult | null>(null);
  const [quantity, setQuantity] = useState('1');
  const [expireDate, setExpireDate] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
      setSelected(null);
      setQuantity('1');
      setExpireDate('');
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (q.length < 2) { setResults([]); return; }
    setSearching(true);
    debounceRef.current = setTimeout(() => {
      searchProducts(q)
        .then(setResults)
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  const handleSave = async () => {
    if (!selected) return;
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    setSaving(true);
    setError(null);
    try {
      await addPackageItem(packageId, {
        productId: selected.id,
        quantity: qty,
        expireDate: expireDate || undefined,
      });
      onSaved();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Add Product to Package"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={() => void handleSave()} disabled={!selected || saving} loading={saving}>
            Add Product
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
        )}

        {/* Product search */}
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-slate-700">Product <span className="text-red-500">*</span></label>
          {selected ? (
            <div className="flex items-center justify-between rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{selected.name}</p>
                {selected.categoryTitle && (
                  <p className="text-xs text-slate-500">{selected.categoryTitle}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => { setSelected(null); setQuery(''); }}
                className="ml-2 text-xs font-medium text-blue-600 hover:text-blue-800"
              >
                Change
              </button>
            </div>
          ) : (
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 pointer-events-none" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search products..."
                autoFocus
                className="w-full rounded border border-slate-300 py-1.5 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              {(results.length > 0 || searching) && (
                <div className="absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
                  {searching && results.length === 0 ? (
                    <div className="flex items-center gap-2 px-3 py-3 text-xs text-slate-400">
                      <Loader2 className="h-3 w-3 animate-spin" /> Searching...
                    </div>
                  ) : (
                    results.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => { setSelected(p); setResults([]); setQuery(''); }}
                        className="flex w-full items-center justify-between px-3 py-2 text-left hover:bg-slate-50"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-slate-800 truncate">{p.name}</p>
                          {p.categoryTitle && (
                            <p className="text-xs text-slate-400 truncate">{p.categoryTitle}</p>
                          )}
                        </div>
                        <span className="ml-2 flex-shrink-0 text-xs font-medium text-slate-500">
                          {fmtCurrency(p.price)}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Quantity */}
        <div className="space-y-1.5">
          <label className="block text-xs font-medium text-slate-700">Quantity <span className="text-red-500">*</span></label>
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-24 rounded border border-slate-300 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        {/* Expiration date */}
        <div className="space-y-1.5">
          <label htmlFor="package-expire-date" className="block text-xs font-medium text-slate-700">Expiration Date</label>
          <DatePicker
            id="package-expire-date"
            value={expireDate}
            min={today}
            onChange={setExpireDate}
            clearable
            wrapperClassName="w-48"
          />
          <p className="text-xs text-slate-400">Leave empty for no expiration</p>
        </div>
      </div>
    </Modal>
  );
}

/* ── Package Title Card ───────────────────────────────────────────── */

function PackageTitleCard({ pkg, discounts }: { pkg: PatientPackage; discounts: PaymentDiscount[] }) {
  const giftCardTotal = (pkg.giftCards ?? []).reduce((sum, gc) => sum + gc.redeemedAmount, 0);
  const creditApplied = pkg.creditAmount ?? 0;
  const chargeAmount = pkg.chargeAmount ?? 0;
  const subtotal = pkg.price;
  const discount = subtotal - chargeAmount - giftCardTotal - creditApplied;
  const hasDiscount = discount > 0.005;
  const items = pkg.items ?? [];

  // The exact named discounts applied to this package, mirroring how product
  // line items surface their discounts. Falls back to the derived total below
  // when no named discount was persisted (e.g. legacy price overrides).
  const appliedDiscounts = [
    { id: pkg.discount1Id ?? null, amount: pkg.manualDiscount1 ?? 0 },
    { id: pkg.discount2Id ?? null, amount: pkg.manualDiscount2 ?? 0 },
  ]
    .filter((d) => d.id && d.amount > 0.005)
    .map((d) => {
      const disc = discounts.find((x) => x.paymentDiscountID === d.id);
      const pct = disc?.discountType === 2 ? `(${disc.discountAmount}%) ` : '';
      return { label: `${pct}${disc?.title ?? `Discount #${d.id}`}`, amount: d.amount };
    });

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-slate-500">Charged Amount</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">
            {chargeAmount === 0 && giftCardTotal > 0
              ? fmtCurrency(giftCardTotal)
              : chargeAmount === 0
                ? '$0.00'
                : fmtCurrency(chargeAmount)}
          </p>
          {chargeAmount === 0 && giftCardTotal > 0 && (
            <p className="mt-0.5 text-xs text-slate-500">paid via gift card</p>
          )}
          {chargeAmount > 0 && giftCardTotal > 0 && (
            <p className="mt-1 text-xs text-slate-500">
              + {fmtCurrency(giftCardTotal)} gift card
            </p>
          )}
          {creditApplied > 0 && (
            <p className="mt-0.5 text-xs text-slate-500">
              + {fmtCurrency(creditApplied)} credit applied
            </p>
          )}
        </div>

        <div className="flex-shrink-0 text-right">
          <div className="text-xs font-medium text-slate-500">Purchase Date</div>
          <div className="mt-1 text-sm text-slate-800">{fmtDate(pkg.purchaseDate)}</div>
          <div className="mt-2 text-xs text-slate-500">
            {chargeAmount === 0 && giftCardTotal > 0
              ? 'Gift Card'
              : chargeAmount === 0
                ? 'Comp'
                : pkg.paymentMethodTitle || '—'}
          </div>
          {chargeAmount > 0 && pkg.merchantTitle && (
            <div className="text-xs text-slate-500">{pkg.merchantTitle}</div>
          )}
          {pkg.planId ? (
            <div className="mt-2">
              <a href={`/plans/${pkg.planId}`} className="text-xs text-blue-600 hover:underline" target="_blank" rel="noreferrer">
                Plan #{pkg.planId}
              </a>
            </div>
          ) : null}
        </div>
      </div>

      {/* Package Contents */}
      {items.length > 0 && (() => {
        const grouped = items.reduce<Record<string, { quantity: number; usesRemaining: number; usesTotal: number }>>((acc, item) => {
          const key = item.itemName;
          if (!acc[key]) {
            acc[key] = { quantity: 0, usesRemaining: 0, usesTotal: 0 };
          }
          acc[key].quantity += item.quantity;
          acc[key].usesRemaining += item.usesRemaining ?? 0;
          acc[key].usesTotal += item.usesTotal ?? 0;
          return acc;
        }, {});
        const consolidated = Object.entries(grouped);
        return (
          <div className="mt-4 border-t border-slate-100 pt-3">
            <p className="mb-2 text-xs font-medium text-slate-400">Package Contents</p>
            <div className="space-y-1">
              {consolidated.map(([name, totals]) => (
                <div key={name} className="flex items-baseline gap-3 text-sm">
                  <span className="w-6 text-right font-semibold text-slate-900 tabular-nums">{totals.quantity}</span>
                  <span className="text-slate-700">{name}</span>
                  {totals.usesRemaining < totals.usesTotal && (
                    <span className="text-xs text-slate-400">
                      ({totals.usesRemaining} remaining)
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Itemization */}
      <div className="mt-4 rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm space-y-1">
        <div className="flex justify-between">
          <span className="text-slate-600">Subtotal (Package Price):</span>
          <span className="text-slate-800">{fmtCurrency(subtotal)}</span>
        </div>
        {appliedDiscounts.length > 0 ? (
          appliedDiscounts.map((d, i) => (
            <div key={`disc-${i}`} className="flex justify-between text-amber-700">
              <span>{d.label}:</span>
              <span>−{fmtCurrency(d.amount)}</span>
            </div>
          ))
        ) : hasDiscount ? (
          <div className="flex justify-between text-amber-700">
            <span>Discount:</span>
            <span>−{fmtCurrency(discount)}</span>
          </div>
        ) : null}
        {giftCardTotal > 0 && (
          <div className="flex justify-between text-purple-700">
            <span>Gift Card{(pkg.giftCards ?? []).length > 1 ? 's' : ''} Applied:</span>
            <span>−{fmtCurrency(giftCardTotal)}</span>
          </div>
        )}
        {creditApplied > 0 && (
          <div className="flex justify-between text-blue-700">
            <span>Credit Applied:</span>
            <span>−{fmtCurrency(creditApplied)}</span>
          </div>
        )}
        <div className="flex justify-between font-semibold border-t border-slate-300 pt-1">
          <span className="text-slate-800">Amount Charged:</span>
          <span className="text-slate-900">{fmtCurrency(chargeAmount)}</span>
        </div>
      </div>
    </div>
  );
}

/* ── Main Modal ────────────────────────────────────────────────────── */

interface PackageDetailModalProps {
  pkg?: PatientPackage | null;
  patientId: number;
  patientPackageId?: number | null;
  patientName?: string;
  readonly?: boolean;
  open: boolean;
  onClose: () => void;
}

export function PackageDetailModal({
  pkg = null,
  patientId,
  patientPackageId = null,
  patientName,
  readonly = false,
  open,
  onClose,
}: PackageDetailModalProps) {
  const queryClient = useQueryClient();
  const { hasMinUserLevel } = useAuth();
  const canDelete = hasMinUserLevel(4);
  const canAddProduct = hasMinUserLevel(3);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [productRefreshKey, setProductRefreshKey] = useState(0);

  const detailQuery = useQuery({
    queryKey: ['patient-package-detail', patientId, patientPackageId],
    queryFn: async () => {
      const packages = await getPatientPackages(patientId, { includeInactive: true });
      return packages.find((item) => item.patientPackageId === patientPackageId) ?? null;
    },
    enabled: open && !pkg && patientId > 0 && !!patientPackageId,
  });
  const resolvedPkg = pkg ?? detailQuery.data ?? null;

  const { data: facilities = [] } = useQuery({
    queryKey: ['patient-facilities'],
    queryFn: listPatientFacilities,
    staleTime: 5 * 60_000,
  });

  const { data: discounts = [] } = useQuery({
    queryKey: ['payment-discounts'],
    queryFn: () => listDiscounts(),
    staleTime: 5 * 60_000,
  });

  const facilityName = useMemo(() => {
    const fId = resolvedPkg?.facilityId;
    if (!fId) return null;
    return facilities.find((f) => f.facilityId === fId)?.facilityName ?? null;
  }, [resolvedPkg?.facilityId, facilities]);

  const deletePackageMutation = useMutation({
    mutationFn: (_?: void) => deletePatientPackage(resolvedPkg!.paymentId ?? resolvedPkg!.patientPackageId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['patient-packages', patientId] });
      void queryClient.invalidateQueries({ queryKey: ['patient-package-detail', patientId, patientPackageId] });
      onClose();
    },
  });

  const [productData, setProductData] = useState<ProductTypesResponse | null>(null);

  useEffect(() => {
    if (!open || !patientId) { setProductData(null); return; }
    let cancelled = false;
    getPatientProductTypes(String(patientId)).then((res: ProductTypesResponse) => {
      if (!cancelled) setProductData(res);
    }).catch(() => { /* ignore */ });
    return () => { cancelled = true; };
  }, [open, patientId, productRefreshKey]);

  const filteredProducts = useMemo(() => {
    if (!productData || !resolvedPkg) return { types: [], others: [] };
    return filterProductsByPackage(productData, resolvedPkg.patientPackageId);
  }, [productData, resolvedPkg]);

  const procedureGroups = useMemo(() => groupPackageProcedures(resolvedPkg?.items ?? []), [resolvedPkg]);

  if (!open) return null;

  if (detailQuery.isLoading && !resolvedPkg) {
    return (
      <Modal isOpen={open} onClose={onClose} title="Package" size="xl">
        <div className="py-10 text-center text-sm text-slate-500">Loading package details...</div>
      </Modal>
    );
  }

  if (!resolvedPkg) {
    return (
      <Modal isOpen={open} onClose={onClose} title="Package" size="xl">
        <div className="py-10 text-center text-sm text-slate-500">Package details could not be loaded.</div>
      </Modal>
    );
  }

  const appliedGiftCards = resolvedPkg.giftCards ?? [];

  function handleDeletePackage() {
    if (!window.confirm('Deactivate this package? It will be marked as inactive.')) return;
    deletePackageMutation.mutate(undefined);
  }

  const modalTitle = (
    <div className="flex items-center gap-3">
      <div className="min-w-0">
        {patientName && (
          <Link
            to={`/patients/${patientId}`}
            className="text-xs font-medium text-blue-600 hover:underline"
            target="_blank"
            onClick={(e) => e.stopPropagation()}
          >
            {patientName}
          </Link>
        )}
        <div className="truncate text-base font-bold text-slate-900">
          {resolvedPkg.packageName}
        </div>
      </div>
      <span className={`ml-auto mr-4 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${
        resolvedPkg.declined
          ? 'bg-amber-50 text-amber-600'
          : resolvedPkg.active
            ? 'bg-green-50 text-green-600'
            : 'bg-slate-100 text-slate-500'
      }`}>
        {resolvedPkg.declined ? 'Declined' : resolvedPkg.active ? 'Active' : 'Inactive'}
      </span>
    </div>
  );

  const footerContent = !readonly && canDelete && resolvedPkg.active && !resolvedPkg.declined ? (
    <div className="flex w-full items-center justify-between">
      <Button
        size="sm"
        variant="danger"
        leftIcon={<Trash2 className="h-3.5 w-3.5" />}
        onClick={handleDeletePackage}
        loading={deletePackageMutation.isPending}
      >
        Deactivate Package
      </Button>
      <div />
    </div>
  ) : undefined;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title={modalTitle}
      size="xl"
      footer={footerContent}
      bodyClassName="!p-0 !overflow-hidden flex flex-col"
    >
      {/* Scrollable content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-5 pt-4 space-y-5">

        {/* ── SECTION 1: Package Title Card ── */}
        <PackageTitleCard pkg={resolvedPkg} discounts={discounts} />

        {/* ── SECTION 2: Products (collapsible) ── */}
        <CollapsibleSection
          title="Products"
          defaultOpen
          headerExtra={!readonly && canAddProduct && resolvedPkg.active && !resolvedPkg.declined ? (
            <button
              type="button"
              onClick={() => setAddProductOpen(true)}
              className="flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-slate-500 hover:bg-slate-200 hover:text-slate-700 transition-colors"
              title="Add product"
            >
              <Plus className="h-3.5 w-3.5" />
              Add
            </button>
          ) : undefined}
        >
          {!productData ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
              <span className="ml-2 text-xs text-slate-400">Loading products…</span>
            </div>
          ) : filteredProducts.types.length === 0 && filteredProducts.others.length === 0 ? (
            <p className="py-3 text-center text-xs italic text-slate-400">No products for this package</p>
          ) : (
            <div className="space-y-1 p-2">
              {filteredProducts.types.map((g) => {
                const expDate = getGroupExpireDate(g);
                return (
                  <ProductGroupCard
                    key={`pt-${g.productTypeID}`}
                    group={g}
                    listType="product-types"
                    onAction={async () => {}}
                    readOnly
                    headerExtra={expDate ? <span className="text-slate-400">Expires {fmtShortDate(expDate)}</span> : undefined}
                  />
                );
              })}
              {filteredProducts.others.map((g) => {
                const expDate = getGroupExpireDate(g);
                return (
                  <ProductGroupCard
                    key={`po-${g.productID ?? g.productTypeID}`}
                    group={g}
                    listType="product-others"
                    onAction={async () => {}}
                    readOnly
                    headerExtra={expDate ? <span className="text-slate-400">Expires {fmtShortDate(expDate)}</span> : undefined}
                  />
                );
              })}
            </div>
          )}
        </CollapsibleSection>

        {/* ── Procedures: only when the package was sold with any, used or not ── */}
        {procedureGroups.length > 0 && (
          <CollapsibleSection title="Procedures" defaultOpen>
            <div className="space-y-1 p-2">
              {procedureGroups.map((g) => <ProcedureGroupCard key={g.key} group={g} />)}
            </div>
          </CollapsibleSection>
        )}

        {/* ── SECTION 3: Payment Details (collapsible) ── */}
        <CollapsibleSection title="Payment Details" defaultOpen>
          <div className="space-y-4 p-4">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-0.5 block text-xs font-medium text-slate-500">Payment Method</label>
                <p className="text-sm text-slate-700">{resolvedPkg.paymentMethodTitle || '—'}</p>
              </div>
              <div>
                <label className="mb-0.5 block text-xs font-medium text-slate-500">
                  {resolvedPkg.merchantTitle ? 'Merchant' : 'Facility'}
                </label>
                <p className="text-sm text-slate-700">{resolvedPkg.merchantTitle || facilityName || '—'}</p>
              </div>
              <div>
                <label className="mb-0.5 block text-xs font-medium text-slate-500">Payment ID</label>
                <p className="text-sm text-slate-700">
                  {resolvedPkg.paymentId ? (
                    <button
                      type="button"
                      className="text-blue-600 hover:underline font-medium"
                      onClick={() => setPaymentModalOpen(true)}
                    >
                      #{resolvedPkg.paymentId}
                    </button>
                  ) : '—'}
                </p>
              </div>
              {resolvedPkg.selectedCreditId ? (
                <div>
                  <label className="mb-0.5 block text-xs font-medium text-slate-500">Credit ID</label>
                  <p className="text-sm text-slate-700">#{resolvedPkg.selectedCreditId}</p>
                </div>
              ) : null}
              {resolvedPkg.quoteId ? (
                <div>
                  <label className="mb-0.5 block text-xs font-medium text-slate-500">Quote</label>
                  <p className="text-sm text-slate-700">#{resolvedPkg.quoteId}</p>
                </div>
              ) : null}
            </div>

            {appliedGiftCards.length > 0 ? (
              <div className="space-y-2 rounded border border-slate-200 bg-slate-50 p-3">
                <div className="text-xs font-medium uppercase tracking-wide text-slate-500">Applied Gift Cards</div>
                {appliedGiftCards.map((giftCard) => (
                  <div key={giftCard.giftCardId} className="flex items-center justify-between gap-3 text-sm text-slate-700">
                    <div>
                      <span className="font-medium">{giftCard.serialNumber || `Gift Card #${giftCard.giftCardId}`}</span>
                      {giftCard.title ? <span className="text-slate-500"> • {giftCard.title}</span> : null}
                    </div>
                    <span>{fmtCurrency(giftCard.redeemedAmount)}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </CollapsibleSection>
      </div>

      {resolvedPkg.paymentId && (
        <PaymentDetailModal
          paymentId={paymentModalOpen ? resolvedPkg.paymentId : null}
          patientId={patientId}
          patientName={patientName}
          open={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
        />
      )}

      <AddProductModal
        packageId={resolvedPkg.patientPackageId}
        open={addProductOpen}
        onClose={() => setAddProductOpen(false)}
        onSaved={() => setProductRefreshKey((k) => k + 1)}
      />
    </Modal>
  );
}
